import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface BreadcrumbContextType {
  overrides: Map<string, string>;
  setOverride: (path: string, label: string) => void;
  clearOverride: (path: string) => void;
  getOverride: (path: string) => string | undefined;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Map<string, string>>(new Map());

  const setOverride = useCallback((path: string, label: string) => {
    setOverrides(prev => {
      const next = new Map(prev);
      next.set(path, label);
      return next;
    });
  }, []);

  const clearOverride = useCallback((path: string) => {
    setOverrides(prev => {
      const next = new Map(prev);
      next.delete(path);
      return next;
    });
  }, []);

  const getOverride = useCallback((path: string) => {
    return overrides.get(path);
  }, [overrides]);

  return (
    <BreadcrumbContext.Provider value={{ overrides, setOverride, clearOverride, getOverride }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBreadcrumbOverride(path: string, label: string | undefined) {
  const { setOverride, clearOverride } = useBreadcrumb();

  // Set the override when label changes
  if (label) {
    setOverride(path, label);
  }

  // Return a cleanup function
  return () => clearOverride(path);
}
